import { useState } from "react";
import { Avatar, Menu, Select, UnstyledButton } from "@mantine/core";
import {
  IconChevronDown,
  IconLock,
  IconLogout,
  IconMapPin,
} from "@tabler/icons-react";
import AllsafeIcon from "../../../assets/logoallsafe.svg";
import { useAuth } from "../../../context/auth/useAuth";
import {
  RoboticsView,
  vlcModule,
  logicsModule,
  type RoboticsModule,
} from "../../../features/robotics";
import { ActiveLocationProvider } from "../../../features/robotics/general/activeLocation";
import { ROBOTICS_LOCATIONS } from "../../../features/robotics/general/locations";

interface LocationOption {
  id: string;
  name: string;
  /** Backend location id — the discriminator for docks/units/etc. */
  locationId: number;
  module: RoboticsModule;
}

// Location is the module switch: each location runs one robotics system, so
// selecting it loads that module's bundle and its tabs (Amsterdam → VLC,
// Utrecht → LogiCS). Location id/name come from ROBOTICS_LOCATIONS (single
// source of truth); `module` is the dev shell's stand-in for the host mounting
// one bundle per location.
const LOCATIONS: LocationOption[] = [
  { code: "ARSAN", module: vlcModule },
  { code: "AMOUT", module: logicsModule },
].map(({ code, module }) => {
  const loc = ROBOTICS_LOCATIONS.find((l) => l.code === code);
  if (!loc) throw new Error(`Unknown robotics location code: ${code}`);
  return { id: loc.code.toLowerCase(), name: loc.name, locationId: loc.locationId, module };
});

export default function AuthenticatedLayout() {
  const { logout, user, changePassword } = useAuth();

  const environment = process.env.REACT_APP_ENVIRONMENT || "production";
  const isTestEnv = environment === "test" || environment === "development";

  const [locationId, setLocationId] = useState(LOCATIONS[0].id);
  const activeLocation =
    LOCATIONS.find((l) => l.id === locationId) ?? LOCATIONS[0];
  const activeModule = activeLocation.module;
  const [viewKey, setViewKey] = useState(activeModule.defaultTab);

  const activeTab =
    activeModule.tabs.find((t) => t.value === viewKey) ?? activeModule.tabs[0];

  const handleLocationChange = (id: string | null) => {
    const next = LOCATIONS.find((l) => l.id === id);
    if (!next) return;
    setLocationId(next.id);
    setViewKey(next.module.defaultTab);
  };

  const userLabel = user?.name || user?.email || "";

  return (
    <div className="app-shell flex flex-col bg-[#F7F7F8]">
      <header className="h-[calc(3.5rem+env(safe-area-inset-top))] md:h-16 pt-[env(safe-area-inset-top)] flex-shrink-0 bg-white border-b border-slate-200 flex items-center justify-between gap-3 px-3 md:px-6">
        <div className="flex items-center gap-4 min-w-0">
          <img src={AllsafeIcon} alt="AllSafe" className="h-7 md:h-8 w-auto flex-shrink-0" />
          {activeTab && (
            <span className="hidden md:inline text-lg font-semibold text-slate-900 truncate">
              {activeTab.label}
            </span>
          )}
          {isTestEnv && (
            <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded-md uppercase tracking-wide">
              Test
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="hidden md:inline text-xs font-semibold uppercase tracking-wide text-slate-400">
              Location
            </span>
            <Select
              leftSection={<IconMapPin size={16} />}
              data={LOCATIONS.map((l) => ({ value: l.id, label: l.name }))}
              value={locationId}
              onChange={handleLocationChange}
              allowDeselect={false}
              checkIconPosition="right"
              className="w-40 md:w-[190px]"
              aria-label="Location"
            />
          </div>

          <Menu position="bottom-end" width={220} withArrow>
            <Menu.Target>
              <UnstyledButton className="flex items-center gap-2">
                <Avatar radius="xl" color="primary">
                  {getInitials(userLabel)}
                </Avatar>
                <IconChevronDown size={16} className="hidden md:block text-slate-500" />
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{userLabel}</Menu.Label>
              <Menu.Item
                leftSection={<IconLock size={16} />}
                onClick={changePassword}
              >
                Change Password
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconLogout size={16} />}
                onClick={() => logout()}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </header>

      <ActiveLocationProvider
        value={{
          id: activeLocation.id,
          name: activeLocation.name,
          locationId: activeLocation.locationId,
        }}
      >
      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        <nav className="order-last md:order-none flex-shrink-0 bg-white border-t md:border-t-0 md:border-r border-slate-200 flex md:flex-col md:w-56 pb-[env(safe-area-inset-bottom)] md:pb-0">
          <p className="hidden md:block px-6 pt-6 pb-2 text-xs font-bold uppercase tracking-widest text-slate-400">
            Operations
          </p>
          <ul className="flex w-full md:block">
            {activeModule.tabs.map((t) => {
              const Icon = t.icon;
              const active = viewKey === t.value;
              return (
                <li key={t.value} className="flex-1 min-w-0">
                  <button
                    onClick={() => setViewKey(t.value)}
                    className={`flex flex-col md:flex-row items-center justify-center md:justify-start gap-1 md:gap-3 w-full h-full text-center md:text-left px-1 md:px-6 py-2 md:py-3 text-[11px] leading-tight md:text-sm transition-colors border-t-2 md:border-t-0 md:border-l-2 ${
                      active
                        ? "md:bg-slate-100 text-[#023058] md:text-slate-900 font-semibold border-[#023058]"
                        : "text-slate-500 md:text-slate-600 border-transparent hover:bg-slate-50"
                    }`}
                  >
                    {Icon && <Icon size={20} />}
                    <span>{t.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <main className="p-3 md:p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
            <RoboticsView module={activeModule} view={viewKey} />
          </main>
        </div>
      </div>
      </ActiveLocationProvider>
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.trim().split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
